import { AccessClaims } from '../api/login/types';
import { isUsableMfaPendingSession, MfaPendingSession } from './mfa';

const TOKEN_KEY = 'control-center-token';
const CLAIMS_KEY = 'control-center-claims';
const MFA_KEY = 'control-center-mfa';

/**
 * Without claims the stored ones are dropped: they always describe the token
 * next to them, and a token that came without claims is read as a JWT.
 */
export const setLocalStorageToken = (
  token: string,
  claims?: AccessClaims
): void => {
  localStorage.setItem(TOKEN_KEY, token);
  if (claims) {
    localStorage.setItem(CLAIMS_KEY, JSON.stringify(claims));
  } else {
    localStorage.removeItem(CLAIMS_KEY);
  }
};

export const getLocalStorageToken = (): string | null => {
  const value = localStorage.getItem(TOKEN_KEY);
  return value;
};

export const removeLocalStorageToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(CLAIMS_KEY);
  localStorage.removeItem(MFA_KEY);
};

const isAccessClaims = (value: unknown): value is AccessClaims => {
  if (typeof value !== 'object' || value === null) return false;

  const { exp, user } = value as Partial<AccessClaims>;
  return (
    typeof exp === 'number' &&
    typeof user === 'object' &&
    user !== null &&
    Array.isArray(user.companies)
  );
};

/** Claims of the session token, or null when it came without them. */
export const getLocalStorageClaims = (): AccessClaims | null => {
  const value = localStorage.getItem(CLAIMS_KEY);
  if (!value) return null;

  try {
    const parsed = JSON.parse(value);
    if (isAccessClaims(parsed)) return parsed;
  } catch {
    // Corrupted entry: cleared below, same as a malformed one.
  }

  localStorage.removeItem(CLAIMS_KEY);
  return null;
};

/**
 * An MFA-pending access_token is kept OUT of TOKEN_KEY on purpose: the API only
 * accepts it on /otp/* and /totp/*, and everything that reads the session token
 * (myHeadersWithToken, PrivateRoute) would happily send it anywhere and take a
 * 401. Under its own key the rest of the app reads "no session" and the MFA
 * calls pass the token explicitly.
 */
export const setMfaPendingSession = (session: MfaPendingSession): void => {
  localStorage.setItem(MFA_KEY, JSON.stringify(session));
};

/**
 * Null once the entry no longer describes a login that can be finished — the
 * Login redirects to /mfa on the presence of this key alone, so a spent one
 * would strand the user on a screen no code can complete. The dead entry is
 * dropped on the way out.
 */
export const getMfaPendingSession = (): MfaPendingSession | null => {
  const value = localStorage.getItem(MFA_KEY);
  if (!value) return null;

  try {
    const parsed = JSON.parse(value);
    if (isUsableMfaPendingSession(parsed)) return parsed;
  } catch {
    // Corrupted entry: cleared below, same as a spent one.
  }

  localStorage.removeItem(MFA_KEY);
  return null;
};

export const removeMfaPendingSession = (): void => {
  localStorage.removeItem(MFA_KEY);
};
